// แผนที่โลกแบบ SVG (docs/PLAN.md ข้อ 4.4)
// ชั้นจากล่างขึ้นบน: ทะเล → เส้นละติจูดลองจิจูด → แผ่นดิน → ช่องถิ่นที่อยู่ → เส้นทางชีวิต → จุดเกิด → ตำแหน่งปัจจุบัน

import { useId, useMemo, useState } from "react";
import { cellBounds, type LatLon } from "../../engine/grid";
import { MAP_HEIGHT, MAP_WIDTH, project } from "../../geo/projection";
import type { Box as GroundBox } from "../../data/migrants";
import type { Realm, SpeciesRange } from "../../geo/range";
import type { WorldShapes } from "../geo";

interface Props {
  world: WorldShapes;
  range: SpeciesRange;
  path: readonly LatLon[]; // จุดแรกคือจุดเกิด จุดสุดท้ายคือตำแหน่งปัจจุบัน
  realm: Realm; // ตัดช่องถิ่นที่อยู่ตามเส้นชายฝั่ง สัตว์ทะเลไม่ระบายทับแผ่นดิน สัตว์บกไม่ระบายทับทะเล
  label: string;
  // สัตว์อพยพ: แหล่งผสมพันธุ์ (a) แหล่งหากินนอกฤดู (b) และเส้นทางอพยพจริงที่ผ่านจุดแวะ
  grounds?: { a: readonly GroundBox[]; b: readonly GroundBox[]; flyways?: readonly (readonly LatLon[])[] };
}

interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

const r1 = (n: number) => Math.round(n * 10) / 10;

const NEAR_DEG = 18; // ซูมดูถิ่นที่อยู่เฉพาะส่วนที่อยู่ในระยะนี้จากเส้นทางของเรา

/**
 * โซนถิ่นที่อยู่ซ้อนกันตามความหนาแน่น: zones[1] = ทุกช่อง, zones[2] = ระดับ 2 ขึ้นไป, zones[3] = ระดับ 3
 * วาดซ้อนกันแล้วตรงที่พบบ่อยจะเข้มกว่าขอบนอก
 */
function zonePaths(range: SpeciesRange): string[] {
  const d = ["", "", "", ""];
  for (const { cell, level } of range.cells) {
    const b = cellBounds(cell);
    const pts = [project(b.west, b.north), project(b.east, b.north), project(b.east, b.south), project(b.west, b.south)];
    const square = `M${pts.map(([x, y]) => `${r1(x)} ${r1(y)}`).join("L")}Z`;
    for (let z = 1; z <= Math.min(3, level); z++) d[z] += square;
  }
  return d;
}

// ช่องกริดกว้างราว 2.8 หน่วยบนแผนที่ เบลอแล้วตัดขอบให้คม ช่องสี่เหลี่ยมจะรวมกันเป็นโซนขอบโค้งมน
const ZONE_BLUR = 1.3;
const ZONE_SHARPEN = "0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 16 -6";

/** ฟิลเตอร์ของโซนถิ่นที่อยู่ โซนนอกมีแสงเรืองรอบนอกและเส้นขอบ โซนในเป็นเนื้อสีจางๆ ขอบนุ่ม */
function ZoneFilters({ id, edge }: { id: string; edge: number }) {
  const region = { filterUnits: "userSpaceOnUse" as const, x: -50, y: -50, width: MAP_WIDTH + 100, height: MAP_HEIGHT + 100, colorInterpolationFilters: "sRGB" as const };
  return (
    <>
      <filter id={`${id}-zone`} {...region}>
        <feGaussianBlur in="SourceAlpha" stdDeviation={ZONE_BLUR} result="blur" />
        <feColorMatrix in="blur" type="matrix" values={ZONE_SHARPEN} result="shape" />
        <feGaussianBlur in="shape" stdDeviation={ZONE_BLUR * 1.6} result="haloBlur" />
        <feFlood className="zone-flood" floodOpacity={0.35} />
        <feComposite in2="haloBlur" operator="in" result="halo" />
        <feFlood className="zone-flood" floodOpacity={0.2} />
        <feComposite in2="shape" operator="in" result="fill" />
        <feMorphology in="shape" operator="erode" radius={edge} result="inner" />
        <feComposite in="shape" in2="inner" operator="out" result="ring" />
        <feFlood className="zone-flood" floodOpacity={0.9} />
        <feComposite in2="ring" operator="in" result="edge" />
        <feMerge>
          <feMergeNode in="halo" />
          <feMergeNode in="fill" />
          <feMergeNode in="edge" />
        </feMerge>
      </filter>
      <filter id={`${id}-core`} {...region}>
        <feGaussianBlur in="SourceAlpha" stdDeviation={ZONE_BLUR} result="blur" />
        <feColorMatrix in="blur" type="matrix" values={ZONE_SHARPEN} result="shape" />
        <feGaussianBlur in="shape" stdDeviation={ZONE_BLUR * 0.8} result="soft" />
        <feFlood className="zone-flood" floodOpacity={0.22} />
        <feComposite in2="soft" operator="in" />
      </filter>
    </>
  );
}

/** มุมของช่องถิ่นที่อยู่ที่อยู่ใกล้เส้นทาง ถิ่นที่อยู่ไกลออกไป (อีกทวีป) ดูได้ด้วยปุ่มดูทั้งโลก */
function nearbyCorners(range: SpeciesRange, path: readonly LatLon[]): [number, number][] {
  const out: [number, number][] = [];
  for (const { cell } of range.cells) {
    const b = cellBounds(cell);
    const near = path.some((p) => {
      const dLon = Math.abs(((b.west + 0.5 - p.lon + 540) % 360) - 180);
      return Math.abs(b.south + 0.5 - p.lat) <= NEAR_DEG && dLon <= NEAR_DEG;
    });
    if (near) out.push(project(b.west, b.north), project(b.east, b.south));
  }
  return out;
}

/** กรอบที่ซูมเข้าไปดูถิ่นที่อยู่ สัดส่วน 2:1 ถ้ากว้างเกินครึ่งโลก (หรือข้ามเส้นวัน) แสดงทั้งโลก */
function focusBox(points: [number, number][]): Box {
  const world = { x: 0, y: 0, w: MAP_WIDTH, h: MAP_HEIGHT };
  if (!points.length) return world;
  const xs = points.map((p) => p[0]);
  const ys = points.map((p) => p[1]);
  let x0 = Math.min(...xs);
  let x1 = Math.max(...xs);
  let y0 = Math.min(...ys);
  let y1 = Math.max(...ys);
  if (x1 - x0 > MAP_WIDTH * 0.55) return world;
  const pad = Math.max(x1 - x0, y1 - y0) * 0.15 + 8;
  x0 -= pad;
  x1 += pad;
  y0 -= pad;
  y1 += pad;
  let w = Math.max(x1 - x0, 70);
  let h = Math.max(y1 - y0, 35);
  if (w / h < 2) w = h * 2;
  else h = w / 2;
  if (w >= MAP_WIDTH) return world;
  const cx = Math.min(Math.max((x0 + x1) / 2, w / 2), MAP_WIDTH - w / 2);
  const cy = Math.min(Math.max((y0 + y1) / 2, h / 2), MAP_HEIGHT - h / 2);
  return { x: cx - w / 2, y: cy - h / 2, w, h: Math.min(h, MAP_HEIGHT) };
}

/** กรอบพิกัดของแหล่งอพยพ เส้นลองจิจูดในโปรเจกชัน Equal Earth โค้ง จึงเก็บจุดทุก 2° */
function boxPoints([s, w, n, e]: GroundBox): [number, number][] {
  const pts: [number, number][] = [];
  const steps = (a: number, b: number) => Math.max(1, Math.ceil(Math.abs(b - a) / 2));
  for (let i = 0, k = steps(w, e); i <= k; i++) pts.push(project(w + ((e - w) * i) / k, n));
  for (let i = 1, k = steps(n, s); i <= k; i++) pts.push(project(e, n + ((s - n) * i) / k));
  for (let i = 1, k = steps(e, w); i <= k; i++) pts.push(project(e + ((w - e) * i) / k, s));
  for (let i = 1, k = steps(s, n); i < k; i++) pts.push(project(w, s + ((n - s) * i) / k));
  return pts;
}

const boxPath = (boxes: readonly GroundBox[]) => boxes.map((b) => `M${boxPoints(b).map(([x, y]) => `${r1(x)} ${r1(y)}`).join("L")}Z`).join("");

/** เส้นทาง ตัดเส้นเมื่อข้ามเส้นวันไม่ให้ลากผ่านทั้งแผนที่ */
function routePath(points: [number, number][]): string {
  return points.map(([x, y], i) => `${i && Math.abs(x - points[i - 1][0]) < MAP_WIDTH / 2 ? "L" : "M"}${r1(x)} ${r1(y)}`).join("");
}

export function WorldMap({ world, range, path, realm, label, grounds }: Props) {
  const [zoomed, setZoomed] = useState(true);
  const id = `map${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  const land = useMemo(() => world.countries.map((c) => c.d).join(""), [world]);
  const zones = useMemo(() => zonePaths(range), [range]);
  const projected = useMemo(() => path.map((p) => project(p.lon, p.lat)), [path]);
  const groundCorners = useMemo(
    () => (grounds ? [...[...grounds.a, ...grounds.b].flatMap(boxPoints), ...(grounds.flyways ?? []).flat().map((p) => project(p.lon, p.lat))] : []),
    [grounds],
  );
  const focus = useMemo(() => focusBox([...nearbyCorners(range, path), ...projected, ...groundCorners]), [range, path, projected, groundCorners]);
  const box = zoomed ? focus : { x: 0, y: 0, w: MAP_WIDTH, h: MAP_HEIGHT };
  const isWorld = focus.w === MAP_WIDTH;
  const dot = Math.max(box.w / 90, 1.2);
  // เส้นขอบโซนหนาราว 1.5 พิกเซลไม่ว่าจะซูมแค่ไหน (แผนที่กว้างราว 360 พิกเซลบนมือถือ) แต่ไม่หนาเกินครึ่งช่องกริด
  const edge = Math.min(0.8, Math.max(0.25, (box.w / 360) * 1.5));
  const here = projected[projected.length - 1];
  const birth = projected[0];

  return (
    <figure className="map">
      <svg viewBox={`${r1(box.x)} ${r1(box.y)} ${r1(box.w)} ${r1(box.h)}`} role="img" aria-label={label} preserveAspectRatio="xMidYMid meet">
        <path className="map-sea" d={world.sphere} />
        <path className="map-grid" d={world.graticule} vectorEffect="non-scaling-stroke" />
        <path className="map-land" d={land} vectorEffect="non-scaling-stroke" />
        <defs>
          <clipPath id={`${id}-land`}>
            <path d={land} />
          </clipPath>
          <mask id={`${id}-sea`}>
            <path d={world.sphere} fill="white" />
            <path d={land} fill="black" />
          </mask>
          <ZoneFilters id={id} edge={r1(edge * 100) / 100} />
        </defs>
        <g clipPath={realm === "land" ? `url(#${id}-land)` : undefined} mask={realm === "sea" ? `url(#${id}-sea)` : undefined}>
          {zones[1] && <path className="map-zone" d={zones[1]} filter={`url(#${id}-zone)`} />}
          {zones[2] && <path className="map-zone" d={zones[2]} filter={`url(#${id}-core)`} />}
          {zones[3] && <path className="map-zone" d={zones[3]} filter={`url(#${id}-core)`} />}
        </g>
        {grounds && (
          <>
            <path className="map-ground a" d={boxPath(grounds.a)} vectorEffect="non-scaling-stroke" />
            <path className="map-ground b" d={boxPath(grounds.b)} vectorEffect="non-scaling-stroke" />
            {grounds.flyways?.map((line, i) => (
              <path key={i} className="map-flyway" d={routePath(line.map((p) => project(p.lon, p.lat)))} vectorEffect="non-scaling-stroke" />
            ))}
          </>
        )}
        {projected.length > 1 && <path className="map-route" d={routePath(projected)} vectorEffect="non-scaling-stroke" />}
        {birth && projected.length > 1 && <circle className="map-birth" cx={birth[0]} cy={birth[1]} r={dot * 0.8} vectorEffect="non-scaling-stroke" />}
        {here && (
          <g className="map-here" transform={`translate(${r1(here[0])} ${r1(here[1])})`}>
            <circle className="pulse" r={dot * 2.4} />
            <circle r={dot} vectorEffect="non-scaling-stroke" />
          </g>
        )}
      </svg>
      {!isWorld && (
        <button type="button" className="map-zoom" onClick={() => setZoomed(!zoomed)}>
          {zoomed ? "ดูทั้งโลก" : "ซูมเข้าไป"}
        </button>
      )}
    </figure>
  );
}
